import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, RotateCcw, Lock, Loader2 } from "lucide-react";
import { getSignatureTask, submitSignature } from "../lib/signatureClient";
import { supabase } from "../../lib/supabase";
import { contractIsFullySigned, finalizeSignedContract } from "../lib/contractEngine";
import { LOGO_URL } from "../../lib/data";
import Markdown from "../../components/Markdown";

const ROLE_LABEL = {
  manager: "Manager",
  label: "Label",
  artist: "Künstler",
  feature: "Feature-Künstler",
  creator: "Creator",
  customer: "Kunde / Vertragspartner",
  external: "Externer Vertragspartner",
};

export default function SignPage() {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState("read"); // "read" | "sign"
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreeDsgvo, setAgreeDsgvo] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = await getSignatureTask(token);
        setTask(t);
        if (t?.signed_at) setDone(true);
      } catch (e) {
        toast.error("Ungültiger oder abgelaufener Link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Canvas setup — läuft NEU sobald das Canvas sichtbar ist (step==="sign")
  useEffect(() => {
    if (step !== "sign") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      // Preserve current drawing before resize
      const prev = document.createElement("canvas");
      prev.width = canvas.width;
      prev.height = canvas.height;
      if (canvas.width && canvas.height) {
        prev.getContext("2d").drawImage(canvas, 0, 0);
      }

      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);

      const ctx = canvas.getContext("2d");
      // WICHTIG: Reset transform, damit setTransform sich nicht stapelt
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#111";

      // Draw previous strokes back (skaliert)
      if (prev.width && prev.height) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(prev, 0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.restore();
      }
    };

    // Warte einen Frame, damit das Layout stabil ist
    const raf = requestAnimationFrame(resize);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [step, task]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const submit = async () => {
    if (!hasStrokes) { toast.error("Bitte unterschreibe zuerst"); return; }
    if (!name.trim()) { toast.error("Bitte gib deinen vollständigen Namen ein"); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const res = await submitSignature(token, name.trim(), dataUrl);
      if (res?.ok) {
        // Nach dem Signieren: Prüfen ob alle Parteien fertig sind → Finalize
        try {
          const { data: sig } = await supabase
            .from("drive_signatures")
            .select("contract_id")
            .eq("token", token)
            .maybeSingle();
          if (sig?.contract_id) {
            const fully = await contractIsFullySigned(sig.contract_id);
            if (fully) {
              await finalizeSignedContract(sig.contract_id);
            }
          }
        } catch (finalizeErr) {
          console.warn("Finalize nach Sign fehlgeschlagen:", finalizeErr);
        }
        setDone(true);
        toast.success("Vielen Dank — deine Unterschrift wurde gespeichert.");
      } else {
        toast.error(res?.error === "not_found_or_already_signed" ? "Dieser Link wurde bereits verwendet" : "Fehler beim Speichern");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-400 flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <img src={LOGO_URL} alt="Straßenwert" className="h-16 w-auto mx-auto mb-6 drop-shadow-[0_0_18px_rgba(255,0,51,0.4)]" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#ff0033] font-bold mb-3">Fehler</div>
          <div className="font-display font-black uppercase text-3xl mb-3">Ungültiger Link</div>
          <div className="text-sm text-zinc-500">Dieser Signatur-Link existiert nicht oder ist abgelaufen. Wende dich an Straßenwert.</div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center">
          <img src={LOGO_URL} alt="Straßenwert" className="h-16 w-auto mx-auto mb-6 drop-shadow-[0_0_18px_rgba(255,0,51,0.4)]" />
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
            <Check size={30} className="text-emerald-400" />
          </div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-400 font-bold mb-3">Erledigt</div>
          <div className="font-display font-black uppercase text-3xl mb-3">Unterschrift bestätigt</div>
          <div className="text-sm text-zinc-500 mb-6">Deine Unterschrift wurde gespeichert. Falls schon alle Parteien unterschrieben haben, kannst du die finale Version unten herunterladen.</div>
          <SignedDownloadButton token={token} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white grain">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#ff0033]/10 to-transparent border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-4">
          <img src={LOGO_URL} alt="Straßenwert" className="h-10 w-auto drop-shadow-[0_0_12px_rgba(255,0,51,0.5)]" />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold">Digitale Unterschrift</div>
            <div className="font-display font-black uppercase text-xl md:text-2xl truncate">{task.contract_title}</div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
            <Lock size={12} className="text-[#ff0033]" /> SSL-verschlüsselt
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Du unterschreibst als</div>
          <div className="inline-flex items-center gap-3 bg-[#0F0F0F] border border-[#ff0033]/40 px-5 py-3">
            <span className="red-led" />
            <span className="font-display font-black uppercase text-lg">{task.signer_label || ROLE_LABEL[task.signer_role] || task.signer_role}</span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-4 text-[10px] tracking-[0.25em] uppercase font-bold">
          <div className={`flex items-center gap-2 ${step==="read" ? "text-white" : "text-emerald-400"}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center ${step==="read" ? "border-[#ff0033] bg-[#ff0033]/20 text-white" : "border-emerald-500 bg-emerald-500/20"}`}>
              {step==="read" ? "1" : <Check size={12} />}
            </span>
            Vertrag lesen
          </div>
          <span className="h-px flex-1 bg-white/10" />
          <div className={`flex items-center gap-2 ${step==="sign" ? "text-white" : "text-zinc-600"}`}>
            <span className={`w-6 h-6 rounded-full border flex items-center justify-center ${step==="sign" ? "border-[#ff0033] bg-[#ff0033]/20 text-white" : "border-white/15"}`}>
              2
            </span>
            Unterschreiben
          </div>
        </div>

        {/* Contract Body — always visible */}
        <div
          className="bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-8 max-h-[520px] overflow-y-auto"
          data-testid="sign-contract-body"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolledToEnd(true);
          }}
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-4">Vertragstext</div>
          {task.contract_body && task.contract_body.trim().length > 20 ? (
            <>
              <div className="text-sm text-zinc-200 leading-relaxed sign-md">
                <Markdown source={task.contract_body} />
              </div>
              <div className="mt-6 pt-4 border-t border-white/[0.06] text-[10px] tracking-[0.25em] uppercase text-zinc-600 text-center">
                — Ende des Vertragstextes —
              </div>
            </>
          ) : (
            <div className="py-8 text-center space-y-3">
              <div className="text-sm text-[#ff0033] font-bold">⚠ Vertragstext konnte nicht geladen werden</div>
              <div className="text-xs text-zinc-500 max-w-md mx-auto">
                Bei diesem Vertrag ist der Inhalt leer. Bitte kontaktiere <span className="text-white">Straßenwert Records</span>, damit ein neuer Vertrag mit vollständigem Text erstellt wird.
              </div>
            </div>
          )}
        </div>

        {step === "read" && (
          <div className="space-y-4 pt-2 border-t border-white/[0.06]">
            {!scrolledToEnd && (
              <div className="text-xs text-zinc-500 italic flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff0033] animate-pulse" />
                Bitte scrolle den Vertragstext bis zum Ende, um fortzufahren.
              </div>
            )}
            <button
              onClick={() => setStep("sign")}
              disabled={!scrolledToEnd}
              data-testid="sign-continue"
              className="group relative inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.55)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="relative z-10 group-hover:text-white transition-colors">Ich habe gelesen — weiter zur Unterschrift →</span>
              <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
          </div>
        )}

        {step === "sign" && (
          <>
            {/* Sign Area */}
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Deine Unterschrift</div>
              <div className="bg-white border-2 border-dashed border-zinc-300 relative overflow-hidden" style={{ height: 200 }}>
                <canvas
                  ref={canvasRef}
                  data-testid="sign-canvas"
                  className="w-full h-full touch-none cursor-crosshair"
                  onMouseDown={start}
                  onMouseMove={move}
                  onMouseUp={end}
                  onMouseLeave={end}
                  onTouchStart={start}
                  onTouchMove={move}
                  onTouchEnd={end}
                />
                {!hasStrokes && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-zinc-400 text-sm tracking-[0.2em] uppercase font-bold">
                    Hier unterschreiben
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <button onClick={clear} data-testid="sign-clear" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 hover:text-white">
                  <RotateCcw size={12} /> Neu unterschreiben
                </button>
                <button onClick={() => setStep("read")} className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 hover:text-white">
                  ← Zurück zum Vertragstext
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                Vor- und Nachname (Bestätigung)
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="sign-name"
                placeholder="Max Mustermann"
                className="w-full bg-transparent border-b border-white/15 py-3 text-white outline-none focus:border-[#ff0033]/60"
              />
            </div>

            {/* DSGVO Zustimmung */}
            <div className="bg-[#0F0F0F] border border-white/[0.06] p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeDsgvo}
                  onChange={(e) => setAgreeDsgvo(e.target.checked)}
                  data-testid="dsgvo-checkbox"
                  className="mt-0.5 w-5 h-5 accent-[#ff0033] shrink-0"
                />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  Ich willige ein, dass meine personenbezogenen Daten (Name, Unterschrift, Datum) für die Vertragsabwicklung gespeichert und verarbeitet werden.
                  {" "}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#ff0033] underline hover:no-underline" data-testid="dsgvo-link">
                    Datenschutzerklärung ansehen
                  </a>
                </span>
              </label>
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-white/[0.06]">
              <button
                onClick={submit}
                disabled={busy || !agreeDsgvo}
                data-testid="sign-submit"
                className="group relative inline-flex items-center gap-3 bg-[#ff0033] text-white px-8 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.55)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {busy ? "Speichere…" : "Rechtsverbindlich unterschreiben"}
              </button>
              {!agreeDsgvo && (
                <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#ff0033] font-bold">Bitte oben die Datenschutz-Einwilligung setzen</p>
              )}
              <p className="mt-3 text-xs text-zinc-500">
                Mit dem Klick bestätigst du, dass du den Vertragstext gelesen hast und ihm mit einer digitalen Unterschrift zustimmst. Die Unterschrift wird kryptografisch mit Datum gespeichert.
              </p>
            </div>
          </>
        )}
      </div>

      <style>{`
        .sign-md h1, .sign-md h2 { font-size: 16px; font-weight: 700; color: #fff; margin: 20px 0 10px; text-transform: uppercase; letter-spacing: 0.05em; }
        .sign-md h3 { font-size: 14px; font-weight: 700; color: #fff; margin: 16px 0 8px; }
        .sign-md p { margin: 8px 0; color: #d4d4d8; line-height: 1.7; }
        .sign-md strong { color: #fff; font-weight: 700; }
        .sign-md ul { margin: 8px 0 8px 20px; list-style: disc; }
        .sign-md li { margin: 4px 0; color: #d4d4d8; }
        .sign-md hr { border: 0; border-top: 1px solid rgba(255,255,255,0.1); margin: 16px 0; }
        .sign-md a { color: #ff0033; text-decoration: underline; }
      `}</style>
    </div>
  );
}

// Download-Button: baut das PDF mit allen Unterschriften clientseitig neu
// (funktioniert für anonyme Signer trotz RLS via SECURITY-DEFINER RPC)
const SignedDownloadButton = ({ token }) => {
  const [state, setState] = useState("loading"); // loading | ready | waiting | error
  const [building, setBuilding] = useState(false);
  const dataRef = useRef({ task: null, sigs: [] });

  useEffect(() => {
    (async () => {
      try {
        const task = await getSignatureTask(token);
        const { data: sigs, error } = await supabase.rpc("get_all_signatures_for_token", { t: token });
        if (error) throw error;
        dataRef.current = { task, sigs: sigs || [] };
        const allSigned = (sigs || []).length > 0 && (sigs || []).every((s) => s.status === "signed" && s.signature_png);
        setState(allSigned ? "ready" : "waiting");
      } catch (e) {
        console.warn("Signed download check failed:", e);
        setState("error");
      }
    })();
  }, [token]);

  const download = async () => {
    setBuilding(true);
    try {
      const { task, sigs } = dataRef.current;
      const { markdownToPdf } = await import("../lib/contractEngine");
      const pdf = await markdownToPdf(task.contract_body, task.contract_title, sigs);
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const filename = `${(task.contract_title || "Vertrag").replace(/[^a-zA-Z0-9._-]/g, "_")}-SIGNED.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Fehler beim Erstellen der PDF: " + e.message);
    } finally {
      setBuilding(false);
    }
  };

  if (state === "loading") return <div className="text-xs text-zinc-500">Prüfe finale Version…</div>;
  if (state === "error") return <div className="text-xs text-[#ff0033]">Fehler beim Laden der Unterschriften.</div>;
  if (state === "waiting") return <div className="text-xs text-zinc-500">Sobald alle Parteien unterschrieben haben, ist der finale Vertrag verfügbar.</div>;
  return (
    <button onClick={download} disabled={building} data-testid="download-signed"
       className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase transition-colors disabled:opacity-50">
      {building ? <Loader2 size={12} className="animate-spin" /> : null}
      {building ? "PDF wird erstellt…" : "Unterschriebenes PDF herunterladen"}
    </button>
  );
};
